import { useEffect, useRef } from 'react';

const DEFAULT_DURATION = 500;
const DEFAULT_MOVE_TOLERANCE = 10;
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea';

const useLongPress = (
  onLongPress,
  {
    enabled = true,
    duration = DEFAULT_DURATION,
    moveTolerance = DEFAULT_MOVE_TOLERANCE,
    mediaQuery = '(max-width: 767px)',
  } = {},
) => {
  const callbackRef = useRef(onLongPress);
  callbackRef.current = onLongPress;

  const gestureRef = useRef({ timerId: null, origin: null, didFire: false });

  const cancel = () => {
    window.clearTimeout(gestureRef.current.timerId);
    gestureRef.current.timerId = null;
    gestureRef.current.origin = null;
  };

  useEffect(() => () => cancel(), []);

  const onPointerDown = (event) => {
    gestureRef.current.didFire = false;

    if (!enabled || !callbackRef.current || event.button !== 0) return;
    if (mediaQuery && !window.matchMedia(mediaQuery).matches) return;
    if (event.target.closest?.(INTERACTIVE_SELECTOR)) return;

    gestureRef.current.origin = { x: event.clientX, y: event.clientY };
    gestureRef.current.timerId = window.setTimeout(() => {
      gestureRef.current.timerId = null;
      gestureRef.current.origin = null;
      gestureRef.current.didFire = true;
      callbackRef.current?.();
    }, duration);
  };

  const onPointerMove = (event) => {
    const { origin } = gestureRef.current;
    if (!origin) return;

    const movedFar =
      Math.abs(event.clientX - origin.x) > moveTolerance ||
      Math.abs(event.clientY - origin.y) > moveTolerance;

    if (movedFar) cancel();
  };

  const onContextMenu = (event) => {
    if (!gestureRef.current.timerId && !gestureRef.current.didFire) return;
    event.preventDefault();
  };

  const onClickCapture = (event) => {
    if (!gestureRef.current.didFire) return;

    event.preventDefault();
    event.stopPropagation();
    gestureRef.current.didFire = false;
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onPointerLeave: cancel,
    onContextMenu,
    onClickCapture,
  };
};

export default useLongPress;
